import React from 'react';
import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface ScorePanelProps {
  analysis: {
    cqcScore: number;
    keywordDensity: number;
    entityDensity: number;
    readabilityScore: number;
    wordCount: number;
    questionsTotal: number;
    questionsCovered: number;
    aiOverviewProbability: number;
  };
  isOriginal: boolean;
  originalAnalysis?: any;
}

export const ScorePanel: React.FC<ScorePanelProps> = ({
  analysis,
  isOriginal,
  originalAnalysis
}) => {
  const getScoreColor = (score: number, max: number = 100) => {
    const percentage = (score / max) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  const getDensityColor = (density: number, optimal: [number, number] = [2, 3.5]) => {
    if (density >= optimal[0] && density <= optimal[1]) return 'success';
    if (density < optimal[0] * 0.5 || density > optimal[1] * 1.5) return 'error';
    return 'warning';
  };

  const getImprovement = (original: number, current: number) => {
    if (!original || !current) return 0;
    return Math.round(((current - original) / original) * 100);
  };

  const renderMetric = (
    label: string,
    value: number,
    max: number = 100,
    suffix: string = '%',
    optimal?: [number, number]
  ) => {
    const improvement = !isOriginal && originalAnalysis 
      ? getImprovement(originalAnalysis[label.toLowerCase().replace(/\s+/g, '')], value)
      : 0;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="textSecondary">
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" fontWeight="bold">
              {value.toFixed(1)}{suffix}
            </Typography>
            {!isOriginal && improvement !== 0 && (
              <Chip
                size="small"
                label={`${improvement > 0 ? '+' : ''}${improvement}%`}
                color={improvement > 0 ? 'success' : 'error'}
                icon={improvement > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={optimal ? (value / optimal[1]) * 100 : (value / max) * 100}
          color={optimal ? getDensityColor(value, optimal) : getScoreColor(value, max)}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid size={6}>
          {renderMetric('CQC Score', analysis.cqcScore)}
          {renderMetric('Keyword Density', analysis.keywordDensity, 100, '%', [2, 3.5])}
          {renderMetric('Entity Density', analysis.entityDensity, 100, ' /100w', [3, 5])}
        </Grid>
        <Grid size={6}>
          {renderMetric('Readability', analysis.readabilityScore)}
          {renderMetric('AI Overview Likelihood', analysis.aiOverviewProbability)}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Questions Coverage
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                size="small"
                icon={<CheckIcon />}
                label={`${analysis.questionsCovered}/${analysis.questionsTotal}`}
                color={analysis.questionsCovered >= analysis.questionsTotal * 0.7 ? 'success' : 'warning'}
              />
              <Typography variant="caption">
                {Math.round((analysis.questionsCovered / analysis.questionsTotal) * 100)}% covered
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`${analysis.wordCount} words`}
              variant="outlined"
            />
          </Box>
        </Grid>
      </Grid>
      
      {!isOriginal && originalAnalysis && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon fontSize="small" />
            Overall improvement: {getImprovement(originalAnalysis.cqcScore, analysis.cqcScore)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};